import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi, ApiError } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { useFeed } from '@/lib/use-feed';
import { ArticleFeed, ArticleImage } from '@/components/content';
import { Icon } from '@/components/shell';
import { Section } from './page-parts';
import NotFound from './NotFound';

/**
 * `/:locale/author/:slug` — the byline page (PRD §20): photo, bio, socials, and
 * everything they have written.
 *
 * Authors outlive accounts (the Phase 1 schema's rule), so this page exists for
 * reporters who have long since left — which is why the API deactivates rather than
 * deletes them.
 */
export default function AuthorPage() {
  const { locale, t } = useLocale();
  const { slug = '' } = useParams();

  const { data, isError, error } = useQuery({
    queryKey: queryKeys.author(locale, slug),
    queryFn: () => publicApi.author(locale, slug),
    staleTime: STALE.taxonomy,
  });

  const feed = useFeed(locale, { author: slug, limit: 12 });

  if (isError && error instanceof ApiError && error.status === 404) return <NotFound />;

  const author = data?.author;
  const socials = [
    author?.twitterHandle
      ? { name: 'x' as const, href: `https://x.com/${author.twitterHandle.replace('@', '')}` }
      : null,
    author?.facebookUrl ? { name: 'facebook' as const, href: author.facebookUrl } : null,
    author?.instagramUrl ? { name: 'instagram' as const, href: author.instagramUrl } : null,
  ].filter((entry): entry is { name: 'x' | 'facebook' | 'instagram'; href: string } =>
    Boolean(entry),
  );

  return (
    <>
      <header className="border-b border-line bg-soft px-gutter py-7">
        <div className="flex flex-wrap items-center gap-4">
          {author?.photo ? (
            <ArticleImage
              image={author.photo}
              alt={author.name}
              sizes="72px"
              aspect="aspect-square"
              className="h-18 w-18 shrink-0 overflow-hidden rounded-full"
            />
          ) : null}
          <div className="min-w-0">
            <h1 className="text-4xl font-extrabold text-ink">
              {author?.name ?? slug.replace(/-/g, ' ')}
            </h1>
            {author?.bio && <p className="mt-2 max-w-2xl text-md text-body">{author.bio}</p>}
            {socials.length > 0 && (
              <ul className="mt-3 flex gap-3 text-nav-idle">
                {socials.map((social) => (
                  <li key={social.name}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={social.name}
                      className="block transition hover:text-red"
                    >
                      <Icon name={social.name} size={16} />
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </header>

      <Section>
        <ArticleFeed
          articles={feed.articles}
          isPending={feed.isPending}
          isError={feed.isError}
          hasNextPage={Boolean(feed.hasNextPage)}
          isFetchingNextPage={feed.isFetchingNextPage}
          onLoadMore={() => void feed.fetchNextPage()}
          onRetry={() => void feed.refetch()}
          emptyTitle={t('empty.noStories')}
        />
      </Section>
    </>
  );
}
